import React from 'react';
import { Platform } from 'react-native';
import styled from 'styled-components/native';

import Colors from '../../constants/Colors';
import Spacer from '../../components/Spacer';
import { H4, H5, P } from '../../components/Typography';
import { useUserContext } from '../../context/UserContext';
import { UserUnitSystem } from '../../models/User';
import {
  UserAttribute,
  UserAttributeSource,
  UserAttributeType
} from '../../models/UserAttribute';
import { WearableDataLineChartCard } from '../study/screen-components/plugins/WearableDataLineChartCardPlugin';

const StyledScrollView = styled.ScrollView`
  flex: 1;
  background-color: ${Colors.background};
  padding: 0 16px;
  ${Platform.OS === 'web' ? 'padding-top: 80px;' : ''}
`;

const StyledIntroView = styled.View`
  padding: 16px 4px 8px 4px;
`;

const StyledSectionView = styled.View`
  margin-bottom: 56px;
`;

const StyledTitleView = styled.View`
  padding: 0 8px 8px 8px;
`;

const StyledDescriptionView = styled.View`
  padding: 0 8px 16px 8px;
`;

const PORTRAIT_DAY_COUNT = 7;
const LANDSCAPE_DAY_COUNT = 28;

// Rendered with no participant and no connected wearables so the cards fall
// back to the sample values below.
const NO_ATTRIBUTES: UserAttribute[] = [];
const NO_CONNECTED_SOURCES: UserAttributeSource[] = [];

type MeasurementSample = {
  permission: string;
  title: string;
  dataSource: string;
  description: string;
  values: (number | number[])[];
  maxY: number;
  imperialUnit?: '°C' | 'kg';
};

const measurementSamples: MeasurementSample[] = [
  {
    permission: 'READ_SLEEP',
    title: 'Sleep Duration',
    dataSource: UserAttributeType.Sleep,
    description:
      'How long you slept each night. Seeing your sleep next to your daily check-ins helps you and your study team notice whether anything you are trying is changing your rest.',
    values: [7.2, 6.4, 7.8, 6.9, 8.1, 7.5, 6.8],
    maxY: 10
  },
  {
    permission: 'READ_HEART_RATE',
    title: 'Heart Rate',
    dataSource: UserAttributeType.HeartRate,
    description:
      'Your heart rate over the past week. It gives your study a simple, everyday measure of how your body is responding.',
    values: [64, 71, 62, 68, 74, 66, 63],
    maxY: 100
  },
  {
    permission: 'READ_OXYGEN_SATURATION',
    title: 'Oxygen Saturation',
    dataSource: UserAttributeType.OxygenSaturation,
    description:
      'The oxygen level in your blood. Steady readings are a helpful background signal when a study is watching for changes in how you feel.',
    values: [97, 96, 98, 97, 95, 98, 97],
    maxY: 100
  },
  {
    permission: 'READ_STEPS',
    title: 'Daily Steps',
    dataSource: UserAttributeType.Steps,
    description:
      'How much you moved each day. Activity adds context to the rest of your data, since a busy week can explain a change in your sleep or energy.',
    values: [8432, 10231, 6890, 12045, 9120, 7534, 11210],
    maxY: 14000
  },
  {
    permission: 'READ_BLOOD_GLUCOSE',
    title: 'Blood Glucose',
    dataSource: UserAttributeType.BloodGlucose,
    description:
      'Your glucose readings across the week. Studies that look at food, supplements or medication use this to see how your levels respond.',
    values: [5.4, 6.2, 5.8, 5.1, 6.5, 5.9, 5.6],
    maxY: 10
  },
  {
    permission: 'READ_BODY_TEMPERATURE',
    title: 'Body Temperature',
    dataSource: UserAttributeType.BodyTemperature,
    description:
      'Your temperature each day. Small shifts can be an early sign that something has changed, so it is tracked alongside the symptoms you report.',
    values: [36.6, 36.8, 36.5, 37.2, 36.9, 36.7, 36.6],
    maxY: 40,
    imperialUnit: '°C'
  },
  {
    permission: 'READ_TOTAL_CALORIES_BURNED',
    title: 'Calories Burned',
    dataSource: UserAttributeType.CaloriesBurned,
    description:
      'The energy you burned each day. Paired with your steps, it shows how active you were while taking part.',
    values: [2140, 2380, 1980, 2620, 2250, 2090, 2470],
    maxY: 3000
  },
  {
    permission: 'READ_BLOOD_PRESSURE',
    title: 'Blood Pressure',
    dataSource: UserAttributeType.BloodPressure,
    description:
      'Your systolic and diastolic readings, drawn as two lines. Blood pressure is a common measure in studies of diet, exercise and medication.',
    values: [
      [118, 76],
      [121, 79],
      [116, 74],
      [124, 81],
      [119, 77],
      [117, 75],
      [122, 78]
    ],
    maxY: 160
  },
  {
    permission: 'READ_WEIGHT',
    title: 'Weight',
    dataSource: UserAttributeType.Weight,
    description:
      'Your weight over the week. The trend matters more than any single reading, so the graph is there to show gradual change rather than one number.',
    values: [72.4, 72.2, 72.6, 72.1, 71.9, 72.3, 72.0],
    maxY: 100,
    imperialUnit: 'kg'
  },
  {
    permission: 'READ_NUTRITION',
    title: 'Energy Consumed (Nutrition)',
    dataSource: UserAttributeType.EnergyConsumed,
    description:
      'The energy from the food you have logged. Studies compare it with your activity and symptoms to understand your daily patterns.',
    values: [1980, 2240, 2110, 2350, 2050, 2180, 2290],
    maxY: 3000
  }
];

const toImperial = (value: number, unit: '°C' | 'kg') =>
  Number(
    (unit === '°C' ? (value * 9) / 5 + 32 : value * 2.2046226218).toFixed(1)
  );

const HealthConnectSampleDataScreen: React.FC = () => {
  const { user } = useUserContext();
  const isImperial = user?.unitSystem === UserUnitSystem.Imperial;

  return (
    <StyledScrollView>
      <StyledIntroView>
        <H4 color='dark'>Your Health Measurements</H4>
        <Spacer size={2} />
        <P color='dark'>
          These are the measurements your study can read from your phone or a
          connected device. Sample values are shown here in place of your own
          data.
        </P>
      </StyledIntroView>
      <Spacer size={2} />
      {measurementSamples.map(
        ({
          permission,
          title,
          dataSource,
          description,
          values,
          maxY,
          imperialUnit
        }) => {
          const sample =
            isImperial && imperialUnit
              ? {
                  values: values.map(value =>
                    Array.isArray(value)
                      ? value.map(reading => toImperial(reading, imperialUnit))
                      : toImperial(value, imperialUnit)
                  ),
                  maxY: Math.round(toImperial(maxY, imperialUnit))
                }
              : { values, maxY };
          return (
            <StyledSectionView key={permission}>
              <StyledTitleView>
                <H5 color='dark'>{title}</H5>
              </StyledTitleView>
              <StyledDescriptionView>
                <P color='dark'>{description}</P>
              </StyledDescriptionView>
              <WearableDataLineChartCard
                wearableDataSources={{
                  healthKit: { dataSource },
                  googleFit: { dataSource },
                  healthConnect: { dataSource },
                  fitbit: { dataSource },
                  garmin: { dataSource },
                  oura: { dataSource }
                }}
                sampleValues={sample.values}
                sampleMaxY={sample.maxY}
                portraitDayCount={PORTRAIT_DAY_COUNT}
                landscapeDayCount={LANDSCAPE_DAY_COUNT}
                latestAttributes={NO_ATTRIBUTES}
                connectedWearableSources={NO_CONNECTED_SOURCES}
              />
            </StyledSectionView>
          );
        }
      )}
      <Spacer size={6} />
    </StyledScrollView>
  );
};

export default HealthConnectSampleDataScreen;
